import React, { useContext, useState } from 'react';
import { Card } from 'primereact/card';
import { Avatar } from 'primereact/avatar';
import { Divider } from 'primereact/divider';
import { Button } from 'primereact/button';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { NotificationItem } from './types';
import folder from '../../assets/Folder.png';
import plane from '../../assets/Plane.png';
import './NotificationList.scss';
import { ROLES } from '../../constants';
import { selectCurrentUserManagerLevel } from '../../pages/login/authSlice';
import { TLLandingContext } from '../../features/landingPage/teamLeadLandingPage/TeamLeadLandingPage';

interface NotificationListProps {
    notifications: NotificationItem[];
    onNotificationRead?: (notificationId: number) => void;
    gridLayout?: boolean;
}

const NotificationList: React.FC<NotificationListProps> = ({ 
    notifications, 
    onNotificationRead,
    gridLayout = false
}) => {
    const { viaPage } = useContext(TLLandingContext);
    const userManagerLevel = useSelector(selectCurrentUserManagerLevel);
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [showAll, setShowAll] = useState<boolean>(false);

    const toggleExpand = (notification: NotificationItem) => {
        const isCurrentlyExpanded = expandedId === notification.notification_id;
        const isUnread = !notification.notification_read;

        setExpandedId(isCurrentlyExpanded ? null : notification.notification_id);

        if (!isCurrentlyExpanded && isUnread && onNotificationRead) {
            onNotificationRead(notification.notification_id);
        }
    };

  const visibleNotifications = notifications.filter((n) =>
    showAll ? true : (!n.notification_read || expandedId === n.notification_id)
  );

    return (
        <div
            style={{
                display: ROLES.Agents.includes(userManagerLevel) ? 'inline' : 'grid',
                flexDirection: 'column',
                height: '100%',
                maxHeight: viaPage ? '56vh' : '70vh',
                backgroundColor: '#fff',
                borderRadius: '1rem',
            }}
        >
            {/* Scrollable list */}
            <div className="notification-scroll" style={{ flexGrow: 1, overflowY: 'auto', padding: '1rem' }}>
                {visibleNotifications.length === 0 ? (
                    <div className="no-notification-wrapper">
                        <h5>No Data Available</h5>

                        <div className="illustration">
                            <img src={plane} alt="Plane Left" className="plane plane-left" />
                            <path
                                d="M 0,50 Q 75,0 150,50 T 300,50"
                                stroke="#3F5765"
                                strokeWidth="2"
                                fill="transparent"
                                strokeDasharray="5,5"
                            />
                            <img src={plane} alt="Plane Right" className="plane plane-right" />
                            <img src={folder} alt="No Notifications" className="folder" />
                        </div>
                    </div>
                ) : (
                    <div
                        className={gridLayout ? 'notification-grid' : 'notification-list'}
                        >
                        {visibleNotifications.map((notification) => {
                            const initials = notification.sender_name
                            .split(' ')
                            .map((part) => part[0])
                            .join('')
                            .slice(0, 2)
                            .toUpperCase();

                            const isUnread = !notification.notification_read;

                            return (
                                <Card
                                    key={notification.notification_id}
                                    style={{
                                        width: '100%',
                                        marginBottom: '0',
                                        cursor: 'pointer',
                                        borderRadius: '24px',
                                        border: '1px solid #E0E0E0',
                                    }}
                                    onClick={() => toggleExpand(notification)}
                                >
                                    <div
                                        style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}
                                    >
                                        <Avatar
                                            label={initials}
                                            size="large"
                                            shape="circle"
                                            style={{ backgroundColor: '#A9A5FF', color: '#fff' }}
                                        />
                                        <div style={{ flex: 1 }}>
                                            <div
                                                style={{
                                                    fontWeight: isUnread ? 'bolder' : 'normal',
                                                    color: '#3F5765',
                                                    marginBottom: '0.25rem',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem',
                                                }}
                                            >
                                                {notification.notification_subject}
                                                {isUnread && (
                                                    <i
                                                        className="pi pi-circle-fill"
                                                        style={{ fontSize: '0.8rem', color: '#E23636' }}
                                                    />
                                                )}
                                            </div>
                                            <div style={{ fontSize: '0.85rem', color: '#666' }}>
                                                By {notification.sender_name}
                                            </div>
                                            <div style={{ fontSize: '0.75rem', color: '#999' }}>
                                                {format(
                                                    new Date(notification.create_date),
                                                    'dd - MMMM - yyyy. h:mm a'
                                                )}
                                            </div>

                                            {expandedId === notification.notification_id && (
                                                <>
                                                    <Divider />
                                                    <div
                                                        style={{ fontSize: '0.9rem', color: '#3F5765' }}
                                                        dangerouslySetInnerHTML={{
                                                            __html: notification.notification_text,
                                                        }}
                                                    />
                                                </>
                                            )}
                                        </div>
                                        <i
                                            className="pi pi-chevron-right"
                                            style={{ marginTop: '0.25rem', color: '#999' }}
                                        />
                                    </div>
                                </Card>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Sticky footer for "See All" button */}
            {!showAll && visibleNotifications.length > 0 && (
                <div
                    style={{
                        padding: '1rem',
                        borderTop: '1px solid #E0E0E0',
                        backgroundColor: '#fff',
                        position: 'sticky',
                        bottom: 0,
                    }}
                >
                    <Button
                        label="See All Notifications"
                        onClick={() => setShowAll(true)}
                        className='btn-primary w-100'
                        // style={{
                        //     width: '100%',
                        //     borderRadius: '20px',
                        //     backgroundColor: '#6B6FF3',
                        //     padding: '0.3rem 1rem',
                        //     fontSize: '1rem',
                        //     border: 'none',
                        // }}
                    />
                </div>
            )}
        </div>
    );
};

export default NotificationList;
